import React, { useState } from 'react';
import { MessageSquare, Send, X } from 'lucide-react';

interface ClarificationDialogProps {
  executionId: string;
  reason: string;
  originalQuery: string;
  questions?: string[];
  onSubmit: (response: string) => void;
  onCancel: () => void;
}

export const ClarificationDialog: React.FC<ClarificationDialogProps> = ({
  reason,
  originalQuery,
  questions,
  onSubmit,
  onCancel
}) => {
  const [userResponse, setUserResponse] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const hasQuestions = questions && questions.length > 0;

  const handleSubmit = async () => {
    if (!userResponse.trim()) return;
    setIsSubmitting(true);
    try {
      await onSubmit(userResponse.trim());
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  // Build placeholder based on questions
  const placeholder = hasQuestions && questions.length > 1
    ? `e.g. 1. California  2. Slip and fall`
    : 'Type your answer...';

  return (
    <div data-component="ClarificationDialog" className="bg-amber-50 border border-amber-300 rounded-lg p-4 my-3">
      <div className="flex items-start gap-3">
        <MessageSquare className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0 animate-pulse" />
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-semibold text-amber-900 mb-2">Clarification Needed</h4>

          {hasQuestions ? (
            <ol className="list-decimal list-inside space-y-1 mb-3">
              {questions.map((q, i) => (
                <li key={i} className="text-sm text-amber-800">{q}</li>
              ))}
            </ol>
          ) : (
            <p className="text-sm text-amber-800 mb-3">{reason}</p>
          )}

          <div className="text-xs text-amber-600 mb-3">
            <span className="font-medium">Original query:</span> {originalQuery}
          </div>
          <textarea
            value={userResponse}
            onChange={(e) => setUserResponse(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            rows={3}
            className="w-full px-3 py-2 text-sm border border-amber-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500 bg-white resize-none"
            autoFocus
          />
          <div className="flex items-center justify-end gap-2 mt-2">
            <button
              onClick={onCancel}
              className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
            >
              <X className="w-3.5 h-3.5" />
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={!userResponse.trim() || isSubmitting}
              className="flex items-center gap-1 px-3 py-1.5 text-sm text-white bg-amber-600 rounded-md hover:bg-amber-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
            >
              <Send className="w-3.5 h-3.5" />
              {isSubmitting ? 'Submitting...' : 'Submit'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
